/**
 * @file src/shell/ZoomSlider.js
 * Chỉ báo ZOOM theo lối Mozaik 3D — MỘT cột dọc ở mép TRÁI sân khấu.
 *
 * Gồm ba phần: một RÃNH liền chạy suốt chiều cao, một THANH TRƯỢT sáng chạy trong
 * rãnh đó, và một CHẤM NEO ở chân.
 *
 * Đã qua ba bản, ghi lại để không ai dựng lại nhầm:
 *   · bản 1 — hai cột ở GIỮA hai mép, 12 bậc ngắn. Vị trí lấy từ CSS `.mz-zoombar`
 *     trong dự án cũ, nhưng khối CSS đó chưa từng được dùng nên không phải bằng chứng.
 *   · bản 2 — hai cột ở hai GÓC DƯỚI, 5 vạch dài sáng dần từ dưới lên. Đúng vị trí
 *     theo ảnh mozaWeb, nhưng cột PHẢI đụng cột công cụ nên đã bỏ.
 *   · bản 3 (hiện tại) — MỘT cột trái, rãnh LIỀN, thanh trượt DI CHUYỂN trong rãnh
 *     thay vì cộng dồn từng bậc.
 *
 * MÀU theo độ sáng nền, do Shell gắn class `is-stage-dark` lên root:
 *   nền tối  → chấm và thanh trượt TRẮNG
 *   nền sáng → chấm và thanh trượt XANH
 *
 * TỰ ẨN: rãnh và thanh trượt chỉ hiện trong lúc zoom và thêm 1,5s sau khi dừng, rồi mờ
 * đi. CHẤM ở lại vĩnh viễn — nó là mốc cho biết chỗ này có điều khiển. Không để rãnh
 * nằm mãi vì nó chạy suốt chiều cao màn hình, đứng yên thì thành một đường kẻ chắn
 * ngang tầm nhìn.
 *
 * Ba điều đáng lưu ý về cách dựng:
 *  1. Có một <input type="range"> THẬT nằm trên, trong suốt hoàn toàn. Rãnh và thanh
 *     trượt chỉ là lớp VẼ. Nhờ vậy vẫn được điều hướng bàn phím + ARIA miễn phí, không
 *     phải tự dựng lại.
 *  2. Giá trị chuẩn hoá 0..1 với 1 = GẦN NHẤT (thanh trượt lên CAO khi phóng to).
 *     Shell quy đổi sang khoảng cách camera.
 *  3. Hai chiều: kéo/bấm → đổi camera; lăn chuột hoặc pinch trên canvas → engine đổi
 *     camera → Shell gọi setValue() để thanh trượt chạy theo. Trong lúc đang kéo thì
 *     setValue() bị chặn (isDragging) để không giật.
 */

const STEPS = 1000;

/**
 * @typedef {Object} ZoomSliderApi
 * @property {(t01: number) => void} setValue   Đặt mức zoom (0..1). Bỏ qua khi đang kéo.
 * @property {() => number} getValue
 * @property {() => void} retranslate
 * @property {(on: boolean) => void} setCompact
 * @property {HTMLElement} el
 * @property {() => void} dispose
 */

/**
 * @param {Object} o
 * @param {HTMLElement} o.root
 * @param {(k: string, v?: any) => string} o.t
 * @param {(t01: number) => void} o.onInput  Người dùng vừa đổi mức (0..1, 1 = gần nhất)
 * @param {number} [o.step=0.06]             Bước cho lăn chuột
 * @returns {ZoomSliderApi}
 */
export function createZoomSlider(o) {
  const step = o.step ?? 0.06;

  // Hộp bọc phủ cả sân khấu; cột con tự neo vào mép trái. Bọc riêng để dispose() gỡ
  // một lần và để CSS đặt lớp trên cùng.
  const el = document.createElement('div');
  el.className = 'ol-zoom';

  const bar = document.createElement('div');
  bar.className = 'ol-zoombar';

  // RÃNH liền — thay cho 5 vạch rời của bản trước.
  const track = document.createElement('div');
  track.className = 'ol-zoombar__track';
  // THANH TRƯỢT chạy trong rãnh. Vị trí do CSS tính từ biến --ol-zoom-pos (0..1).
  const thumb = document.createElement('div');
  thumb.className = 'ol-zoombar__thumb';
  track.appendChild(thumb);

  // CHẤM NEO ở chân — luôn hiện, kể cả sau khi rãnh đã tự ẩn.
  const dot = document.createElement('span');
  dot.className = 'ol-zoombar__dot';

  const input = document.createElement('input');
  input.type = 'range';
  input.className = 'ol-zoombar__range';
  input.min = '0';
  input.max = String(STEPS);
  input.step = '1';
  input.value = String(STEPS);

  bar.append(track, dot, input);
  el.appendChild(bar);
  o.root.appendChild(el);

  function retranslate() {
    input.setAttribute('aria-label', o.t('zoom.label'));
    input.title = o.t('zoom.label');
  }
  retranslate();

  let isDragging = false;

  /**
   * Hẹn giờ ẩn. Đặt lại mỗi lần giá trị đổi, nên trong lúc zoom liên tục thì rãnh luôn
   * hiện; 1,5s sau nhịp CUỐI mới mờ đi.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  let hideTimer;
  const HIDE_AFTER = 1500;

  function poke() {
    bar.classList.add('is-live');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => bar.classList.remove('is-live'), HIDE_AFTER);
  }

  const read = () => Number(input.value) / STEPS;
  const clamp01 = (/** @type {number} */ v) => Math.max(0, Math.min(1, v));

  /**
   * Giá trị lần ghi trước, do TA tự giữ — KHÔNG đọc lại từ input.value để so sánh.
   * Lý do: khi người dùng kéo, trình duyệt ghi giá trị mới vào input TRƯỚC khi sự kiện
   * 'input' chạy tới handler. So với input.value lúc đó thì lần nào cũng thấy "không
   * đổi", và rãnh sẽ không bao giờ hiện lúc kéo — đúng lỗi đã gặp ở bản trước.
   */
  let lastRaw = String(STEPS);

  /** Đẩy vị trí thanh trượt ra CSS. Mọi phần vẽ đều đi qua biến này. */
  function paint() {
    const v = read();
    bar.style.setProperty('--ol-zoom-pos', String(v));
    input.setAttribute('aria-valuetext', `${Math.round(v * 100)}%`);
  }

  /**
   * Cửa ngõ DUY NHẤT mà mọi thay đổi giá trị đi qua (kéo, lăn, camera đẩy về) — nên
   * poke() đặt ở đây là đủ, không phải rải ra từng chỗ gọi.
   * @param {number} t01
   */
  function write(t01) {
    const raw = String(Math.round(clamp01(t01) * STEPS));
    // Không đổi thì thôi: Shell gọi setValue mỗi khi camera nhúc nhích, nếu poke vô
    // điều kiện thì rãnh không bao giờ ẩn được (xoay mô hình cũng làm nó hiện mãi).
    const changed = raw !== lastRaw;
    lastRaw = raw;
    input.value = raw;
    paint();
    if (changed) poke();
  }

  /**
   * Đích đang tiến tới của lăn chuột, hoặc null khi không có.
   *
   * VÌ SAO CẦN: Engine KHÔNG nhảy tới khoảng cách mới mà lerp mượt về nó
   * (Engine._zoomTargetDistance). Nếu ta ghi THẲNG vị trí rồi mới báo engine thì thanh
   * trượt nhảy một phát tới đích, còn vòng đồng bộ mỗi frame (Shell → setValue theo
   * khoảng cách camera THẬT) lại kéo nó lùi về — mỗi nhịp lăn là một cú giật ngược.
   * Giờ chỉ báo đích cho engine, KHÔNG chạm vào thanh trượt; nó do vòng đồng bộ kéo đi.
   *
   * Vẫn phải nhớ đích vì read() trả về vị trí camera đang LẶNG SAU đích: lăn liên tiếp
   * mà cộng dồn từ read() thì mỗi nhịp chỉ nhích được một phần của `step`.
   * @type {number | null}
   */
  let pending = null;

  /** @param {number} delta */
  function nudge(delta) {
    pending = clamp01((pending ?? read()) + delta);
    o.onInput(pending);
  }

  const onRangeInput = () => {
    // Người dùng tự kéo → đích của họ thắng, bỏ đích đang tiến tới của lăn chuột.
    pending = null;
    const v = read();
    write(v);
    o.onInput(v);
  };
  const onDown = () => {
    isDragging = true;
  };
  const onUp = () => {
    isDragging = false;
  };
  /** @param {WheelEvent} e */
  const onWheel = (e) => {
    e.preventDefault();
    nudge(-Math.sign(e.deltaY) * step);
  };

  input.addEventListener('input', onRangeInput);
  input.addEventListener('pointerdown', onDown);
  // pointerup có thể xảy ra ngoài thanh nếu kéo rồi nhả ở chỗ khác
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
  el.addEventListener('wheel', onWheel, { passive: false });
  paint();
  poke(); // hiện một nhịp lúc mở bài rồi tự ẩn — để học sinh biết chỗ này có gì

  return {
    el,
    setValue(t01) {
      if (isDragging) return;
      // Camera đã tới đích → xoá đích, để lần lăn sau cộng dồn từ vị trí thật.
      if (pending !== null && Math.abs(t01 - pending) < 0.006) pending = null;
      write(t01);
    },
    getValue: read,
    retranslate,
    setCompact(on) {
      el.classList.toggle('is-compact', on);
    },
    dispose() {
      clearTimeout(hideTimer);
      input.removeEventListener('input', onRangeInput);
      input.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      el.removeEventListener('wheel', onWheel);
      el.remove();
    },
  };
}
